import {
  type CaptureMode,
  type InputDefaultKind,
} from '@/models/analytics/features/audio';
import {
  PROCESS_DEVICE_PREFIX,
  SYSTEM_DEFAULT_DEVICE_MARKER,
  SYSTEM_WIDE_DEVICE_MARKER,
} from '@/tauri/constants';
import { type AudioDevice } from 'src-tauri/src/bindings/AudioDevice';

// Display-ready device option for UI rendering
export type DisplayAudioDevice = {
  value: string; // The value to use in select (device name or SYSTEM_DEFAULT_DEVICE_MARKER)
  label: string; // The display label (e.g., "System Default - MacBook Pro Microphone")
  isSystemDefault: boolean; // Whether this is the "System Default" option
  isProcessDevice: boolean; // Whether this is a process loopback device (Windows app)
  iconDataUrl?: string | null; // Base64 PNG data URL for the app icon (process devices only)
  caption?: string; // Secondary descriptive line (the "System audio" disclosure)
};

/**
 * Labels for the virtual "System audio" (whole-system capture) entry.
 * Provided (pre-localized) only when the Windows system-audio-capture
 * flag is ON — see `useAudioDeviceSelector`.
 */
export type SystemWideAudioOption = {
  label: string;
  /** Informed-disclosure line: "Captures all audio playing on this computer". */
  caption: string;
};

/**
 * Check if we're on macOS based on output devices.
 * On macOS, backend returns only "System Audio" for output devices.
 */
export const isMacOSSystemAudio = (devices: AudioDevice[]): boolean => {
  return devices.length === 1 && devices[0]?.name === 'System Audio';
};

/**
 * Check if a device is a process loopback entry (Windows only).
 */
export const isProcessDevice = (device: AudioDevice): boolean => {
  return device.name.startsWith(PROCESS_DEVICE_PREFIX);
};

/**
 * APP-7915/APP-7962: speaker capture mode for `transcription_started`
 * telemetry, derived from the resolved output selection at recording start.
 * Pass `isWindows` from `@/hooks/platform` — kept as a parameter so the
 * derivation stays pure and both branches are unit-testable.
 *
 * Mapping:
 * - macOS → `system_wide` (ScreenCaptureKit captures whole-system audio
 *   regardless of the selected entry).
 * - Windows `__SYSTEM_WIDE__` sentinel → `system_wide` — pass the RESOLVED
 *   output name from `resolveDeviceNamesForStart`, not the raw selection.
 * - Windows app entry (`__PROCESS__:<pid>`) → `process_include`.
 * - Windows device or `__SYSTEM_DEFAULT__` sentinel → `device_loopback`.
 */
export const deriveCaptureMode = (
  outputDevice: AudioDevice,
  isWindowsPlatform: boolean
): CaptureMode => {
  if (!isWindowsPlatform) return 'system_wide';
  if (outputDevice.name === SYSTEM_WIDE_DEVICE_MARKER) return 'system_wide';
  if (isProcessDevice(outputDevice)) return 'process_include';
  return 'device_loopback';
};

/**
 * APP-7915/APP-7962: how the mic selection resolves at recording start, for
 * `transcription_started` telemetry. `comms_default` arrives with WS3
 * (`release-apps-windows-comms-default-mic-desktop`) — until the sentinel is
 * resolved to the communications-default device, the sentinel means the
 * console default.
 */
export const deriveInputDefaultKind = (inputDevice: AudioDevice): InputDefaultKind => {
  return inputDevice.name === SYSTEM_DEFAULT_DEVICE_MARKER
    ? 'console_default'
    : 'explicit';
};

/**
 * Build display-ready device list for UI rendering.
 * Handles "System Default" option logic based on platform.
 *
 * @param devices - Raw device list from backend
 * @param selectedDevice - Currently selected device
 * @param systemDefaultLabel - Localized "System Default" text
 * @returns Object containing displayDevices array, selectedDisplayLabel, and grouped arrays
 */
export const buildDisplayDeviceList = (
  devices: AudioDevice[],
  selectedDevice: AudioDevice | undefined,
  systemDefaultLabel: string,
  getDeviceFallbackLabel?: (index: number) => string,
  systemWideOption?: SystemWideAudioOption,
  /**
   * Input lists only: let a communications-default device stand in for a
   * missing console default, so the "System Default" row (and the selected
   * sentinel's label) still exists on comms-default-only machines — otherwise
   * the pre-selected sentinel has no menu item and can never be reselected.
   */
  commsDefaultEligible?: boolean
): {
  displayDevices: DisplayAudioDevice[];
  selectedDisplayLabel: string;
  speakerDevices: DisplayAudioDevice[];
  appDevices: DisplayAudioDevice[];
} => {
  const defaultDevice =
    devices.find((d) => d.is_default) ??
    (commsDefaultEligible ? devices.find((d) => d.is_communications_default) : undefined);
  const isMacSystemAudio = isMacOSSystemAudio(devices);

  const speakerDevices: DisplayAudioDevice[] = [];
  const appDevices: DisplayAudioDevice[] = [];

  // Virtual "System audio" (whole-system capture) entry, first in the
  // speakers group. Windows-only: the option is only passed when the flag is
  // ON on Windows desktop, and the macOS "System Audio" path is excluded.
  if (systemWideOption && !isMacSystemAudio) {
    speakerDevices.push({
      value: SYSTEM_WIDE_DEVICE_MARKER,
      label: systemWideOption.label,
      isSystemDefault: false,
      isProcessDevice: false,
      iconDataUrl: null,
      caption: systemWideOption.caption,
    });
  }

  // Add "System Default" option first (in speakers group), unless it's macOS System Audio
  if (defaultDevice && !isMacSystemAudio) {
    speakerDevices.push({
      value: SYSTEM_DEFAULT_DEVICE_MARKER,
      label: `${systemDefaultLabel} - ${defaultDevice.display_name ?? defaultDevice.name}`,
      isSystemDefault: true,
      isProcessDevice: false,
      iconDataUrl: null,
    });
  }

  // Partition devices into speakers vs process apps
  const fallback = getDeviceFallbackLabel ?? ((i: number) => `Device ${i + 1}`);
  for (const [index, device] of devices.entries()) {
    const value = device.name || fallback(index);
    const label = (device.display_name ?? device.name) || fallback(index);
    const entry: DisplayAudioDevice = {
      value,
      label,
      isSystemDefault: false,
      isProcessDevice: isProcessDevice(device),
      iconDataUrl: device.icon_data_url,
    };

    if (entry.isProcessDevice) {
      appDevices.push(entry);
    } else {
      speakerDevices.push(entry);
    }
  }

  // Flat list for consumers that don't need grouping
  const displayDevices = [...speakerDevices, ...appDevices];

  // Compute selected display label
  let selectedDisplayLabel = '';
  if (selectedDevice) {
    if (selectedDevice.name === SYSTEM_WIDE_DEVICE_MARKER && systemWideOption) {
      selectedDisplayLabel = systemWideOption.label;
    } else if (
      selectedDevice.name === SYSTEM_DEFAULT_DEVICE_MARKER ||
      // A persisted "System audio" selection with the flag now OFF renders as
      // System Default — matching what start-time resolution actually records
      // with — instead of leaking the raw sentinel.
      selectedDevice.name === SYSTEM_WIDE_DEVICE_MARKER
    ) {
      selectedDisplayLabel = defaultDevice
        ? `${systemDefaultLabel} - ${defaultDevice.display_name ?? defaultDevice.name}`
        : systemDefaultLabel;
    } else {
      selectedDisplayLabel = selectedDevice.display_name ?? selectedDevice.name ?? '';
    }
  }

  return { displayDevices, selectedDisplayLabel, speakerDevices, appDevices };
};
